import { SemanticICONS } from "semantic-ui-react";
import { AbsoluteTimeRange } from "./components/Screens/Dashboards/Datetime/TimeRange";
import { DurationInputArg2 } from "moment";
import { beamtoast } from "./components/common/CustomToast";

declare global {
  interface Window {
    toastr: any;
    _archbee: any;
  }
}

export type Permission = {
  devices: { [key: string]: "all" | string[] };
  tables: { [key: string]: "all" | string[] };
  homepage: string;

  viewMetadata: "all" | string[];
  editMetadata: "all" | string[];

  dashboardPermittedShareRoles: "all" | number[];

  createDashboards: boolean;

  allowedActions: "all" | string[];

  viewFiles: boolean;
  editFiles: boolean;

  viewFirmwares: boolean;
  editFirmwares: boolean;

  viewDeviceConfigs: boolean;
  editDeviceConfigs: boolean;

  viewStreams: boolean;
  editStreams: boolean;

  viewUsers: boolean;
  editUsers: boolean;

  viewRoles: boolean;
  editRoles: boolean;

  viewActionTypes: boolean;
  editActionTypes: boolean;

  showDeviceManagementTab: boolean;
  showDashboardsTab: boolean;
  showActionsTab: boolean;

  viewMetadataKeys: boolean;
  editMetadataKeys: boolean;
  editTenantSettings: boolean;

  allowCreatingDevices: boolean;
  allowMarkActionAsCompleted: boolean;
  reviewActions: boolean;

  // Alerts and Notifications Permissions
  viewAlerts: boolean;
  editAlerts: boolean;
  viewNotifications: boolean;
  editNotifications: boolean;

  // DBC Parsers Permissions
  viewDBCParsers: boolean;
  manageDBCParsers: boolean;
  updateDBCParsing: boolean;
  downloadDBCFiles: boolean;
};

export type AtLeastOnePropRequired<T, K extends keyof T = keyof T> = {
  [Key in K]: Required<Pick<T, Key>> & Partial<Omit<T, Key>>;
}[K];

export type Role = {
  id: number;
  name: string;
  permissions: Permission;
};

export type ApiKey = {
  key?: any;
  role: string;
  name: string;
  created_by: string;
};

export type SimpleConditionOperator =
  | ">"
  | "<"
  | "="
  | "!="
  | ">="
  | "<="
  | "in"
  | "is"
  | "is not";
export type CompositeConditionOperator = "and" | "or";
export type NoDateConditionOperator = "no_data";

export type SimpleCondition = {
  field: string;
  operator: SimpleConditionOperator;
  value: any;
};

export type CompositeCondition = {
  operator: CompositeConditionOperator;
  conditions: Array<Condition>;
};

export type NoDataCondition = {
  operator: NoDateConditionOperator;
  threshold_seconds?: number;
};

export type Condition = SimpleCondition | CompositeCondition | NoDataCondition;

export type ClickhouseToTSTypes =
  | "int"
  | "uint"
  | "float"
  | "string"
  | "boolean"
  | "Date"
  | "null";

/**
 * Converts a ClickHouse data type to a TypeScript data type.
 *
 * @param clickhouseType - The ClickHouse data type as a string.
 * @returns The corresponding TypeScript data type as a string.
 *
 * @remarks
 * This function handles the following ClickHouse data types:
 * - Integers (Int, UInt, Nullable(Int), Nullable(UInt)) are converted to "int".
 * - Floats (Float32, Float64, Nullable(Float)) are converted to "float".
 * - Booleans (Bool, Nullable(Bool)) are converted to "boolean".
 * - Date and timestamp types (Date, DateTime, Nullable(Date), Nullable(DateTime)) are converted to "Date".
 * - Strings (String, Nullable(String)) are converted to "string".
 *
 * If the provided ClickHouse type does not match any of the above, the function defaults to returning "string".
 */
export const getTypeFromClickhouseToTS = (
  clickhouseType: string
): ClickhouseToTSTypes => {
  if (!clickhouseType) {
    return "string";
  }
  // Handle integers (Int, UInt)
  if (
    clickhouseType.startsWith("Int") ||
    clickhouseType.startsWith("Nullable(Int")
  ) {
    return "int";
  }

  if (
    clickhouseType.startsWith("UInt") ||
    clickhouseType.startsWith("Nullable(UInt")
  ) {
    return "uint";
  }

  // Handle floats (Float32, Float64)
  if (
    clickhouseType.startsWith("Float") ||
    clickhouseType.startsWith("Nullable(Float")
  ) {
    return "float";
  }

  // Handle booleans
  if (clickhouseType === "Bool" || clickhouseType === "Nullable(Bool)") {
    return "boolean";
  }

  // Handle date and timestamp types
  if (
    clickhouseType.startsWith("Date") ||
    clickhouseType.startsWith("DateTime") ||
    clickhouseType.startsWith("Nullable(Date") ||
    clickhouseType.startsWith("Nullable(DateTime")
  ) {
    return "Date";
  }

  // Handle strings
  if (clickhouseType === "String" || clickhouseType === "Nullable(String)") {
    return "string";
  }

  // Default to string for any other types
  return "string";
};

/**
 * Returns a filtered list of query operators based on the provided data type.
 *
 * @param {string} type - The data type for which to get the operators.
 *                        Supported types are "int", "float", "Date", "boolean", and "string".
 * @returns {Array} A filtered list of query operators suitable for the given data type.
 *
 * - For "int", "float", and "Date" types, it allows comparison and equality operators.
 * - For "boolean" type, it allows only equality operators and null checks.
 * - For "string" type, it allows only equality operators and null checks.
 * - For any other type, it returns all available query operators.
 */
export const getOperatorsOptionsByDataType = (
  queryOperators: {
    key: string;
    value: string;
    text: string;
  }[],
  type: string,
  isNullable: boolean = false
) => {
  let filteredOperators: {
    key: string;
    value: string;
    text: string;
  }[] = [];

  switch (type) {
    case "int":
    case "float":
    case "Date":
      filteredOperators = queryOperators.filter(
        (op) => ["=", "not=", ">", "<", ">=", "<="].includes(op.value) // Allow comparison and equality for int, float, and date types
      );
      break;
    case "string":
    case "boolean":
      filteredOperators = queryOperators.filter(
        (op) => ["=", "not="].includes(op.value) // Only equality operators for booleans
      );
      break;
    default:
      filteredOperators = queryOperators; // Default case, returns all operators
  }

  if (isNullable) {
    const nullOperators = queryOperators.filter((op) =>
      ["IS NULL", "IS NOT NULL"].includes(op.value)
    );

    filteredOperators = [...filteredOperators, ...nullOperators];
  }

  return filteredOperators;
};

/**
 * Validates the input value based on the specified column type.
 *
 * @param value - The input value to be validated.
 * @param type - The type of the column to validate against.
 * @returns A boolean indicating whether the input value is valid for the specified column type.
 *
 * The function performs the following validations based on the column type:
 * - "int": Validates if the value is a valid integer.
 * - "uint": Validates if the value is a valid whole number (0 or greater).
 * - "float": Validates if the value is a valid integer or floating-point number.
 * - "boolean": Validates if the value is either "true" or "false".
 *
 * If the validation fails, an error message is displayed using a toast notification.
 */
export const isInputValueColumnTypeValid = (
  value: string,
  type: ClickhouseToTSTypes
): boolean => {
  let isValid = true;
  let errorMessage = "";

  switch (type) {
    case "null":
      isValid = value === "null";
      if (!isValid) {
        errorMessage = "Please enter 'null' in the value field.";
      }
      break;
    case "int":
      isValid = validateIntegerNumber(value);
      if (!isValid) {
        errorMessage = "Please enter a valid integer value.";
      }
      break;
    case "uint":
      isValid = validateWholeNumber(value);
      if (!isValid) {
        errorMessage = "Please enter a valid whole number (0 or greater).";
      }
      break;
    case "float":
      isValid = validateIntOrFloatNumber(value);
      if (!isValid) {
        errorMessage =
          "Please enter a valid positive number (integer or floating-point).";
      }
      break;
    case "boolean":
      isValid = value === "true" || value === "false";
      if (!isValid) {
        errorMessage = "Please enter a valid boolean value (true or false).";
      }
      break;

    default:
      isValid = true;
  }

  // Show the toast message if validation fails
  if (!isValid && errorMessage) {
    beamtoast.error(errorMessage);
  }

  return isValid;
};

export type SessionType = {
  name: string;
  stream: string;
  condition: Condition;
  status?: string;
  device_filter: { [key: string]: Array<string | number> };
  last_processed_timestamps?: { [key: number]: string };
};

export type SlackNotificationParams = {
  slack_channel: string;
  bot_token: string;
};

export type WebhookNotificationParams = {
  url: string;
  headers: { [key: string]: string };
};

export type EmailNotificationParams = {
  emails: string[];
  subject?: string;
};

export type SmsNotificationParams = {
  phone_numbers: string[];
};

export type WhatsAppNotificationParams = {
  phone_numbers: string[];
  activation_template_id: string;
  deactivation_template_id: string;
};

export type NotificationChannelParameters =
  | SlackNotificationParams
  | WebhookNotificationParams
  | EmailNotificationParams
  | SmsNotificationParams
  | WhatsAppNotificationParams;

export type AlertNotificationRule = {
  id?: string;
  name: string;
  alert_rule_id: string;
  channel_type: NotificationChannelType;
  channel_parameters: NotificationChannelParameters;
  timezone: string;
  interval_seconds: number;
  last_notified_at?: number;
  activation_template?: string;
  deactivation_template?: string;
  notify_on_deactivation?: boolean;
};

export type AlertRuleCriticality = "critical" | "warning" | "info";

export interface AlertRule {
  id: string;
  name: string;
  criticality: AlertRuleCriticality;
  metadata_filters: { [key: string]: string[] };
  stream: string;
  condition: Condition;
  activation_threshold_seconds: number;
  deactivation_threshold_seconds: number;
  tenant_id?: string;
  group: string;
  status?: string;
  last_processed_timestamps?: Record<number, number | string>;
  notification_rules?: AlertNotificationRule[];
  device_filter: { [key: string]: number[] };
  deepdive_dashboard_id?: string;
}

export type AlertGroups = string[];

export type NotificationChannelType =
  | "slack"
  | "webhook"
  | "email"
  | "sms"
  | "whatsapp";

export interface DBCAdditionalSettings {
  add_suffix_canid?: boolean;
  dont_decode_choices?: boolean;
  allow_duplicate_signals?: boolean;
  lower_threshold?: number | null;
  upper_threshold?: number | null;
  dbc_ver?: number | null;
  add_date_filter?: boolean | null;
}

export interface DBCData {
  dbc_type: DBCTypes;
  name?: string;
  version?: string;
  input_table?: string;
  output_table?: string;
  period: number;
  obd_can_id: number | null;
  j1939_pgns: number[] | null;
  additional_settings?: DBCAdditionalSettings;
}

interface DBCSignals {
  byte_order: string;
  choices: Record<string, any> | null;
  is_signed: boolean;
  is_float: boolean;
  length: number;
  name: string;
  offset: number;
  scale: number;
  start: number;
}

export type EditDBCParserInput = {
  name: string;
  period: number | string;
  version: string;
  additional_settings: {
    dbc_ver?: number | null;
    lower_threshold?: number | null;
    upper_threshold?: number | null;
    allow_duplicate_signals?: boolean;
  };
};

export interface DBCMessageResponse {
  can_id: number;
  name: string;
  signals: DBCSignals[];
}

interface DBCJSONResponse {
  messages: DBCMessageResponse[];
}

export interface ParsedDBCResponse {
  results: { json: DBCJSONResponse };
}

interface DBCFile {
  dbc: string;
}

export interface DBCFileResponse {
  results: DBCFile;
}

export interface AllDBCResponse {
  results: DBCResponse[];
}

export interface SingleDBCResponse {
  results: DBCResponse;
}

export interface DeleteDBCResponse {
  message: string;
}

export interface DBCOffsetResponse {
  [key: string]: number;
}

export type DBCTypes = "custom" | "obd" | "j1939";
export interface DBCResponse {
  id: string;
  dbc_type: DBCTypes;
  name: string;
  tenant_id: string;
  version: string;
  input_table: string;
  output_table: string;
  status: "updating" | "started" | "stopped";
  period: number;
  offsets: DBCOffsetResponse;
  additional_settings: DBCAdditionalSettings | null;
  obd_can_id: number | null;
  j1939_pgns: number[] | null;
}

export type LatencyHistogramStats = {
  range_start: number;
  range_end: number;
  height: number;
}[];

export type ProgressHistogramStats = {
  [key: number]: number[];
};

export interface IRoleResult {
  result: Array<Role>;
}

// Define the type for TabSettings
export type TabSettings = {
  show: boolean;
  [subTabName: string]: TabSettings | boolean;
};

export type ShowTabs = {
  [tabName: string]: TabSettings;
};

// Define the type for ShowTabsData
export type ShowTabsData = {
  device_management: { show: boolean };
  dashboards: { show: boolean };
  actions: {
    show: boolean;
  };
  alerts: {
    show: boolean;
  };
  dbc_parsers: {
    show: boolean;
  };
  device_components: {
    show: boolean;
  };
};

export type CustomTimeRanges = {
  [x: string]:
    | AbsoluteTimeRange
    | {
        to: {
          date: string;
        };
        from: {
          date: string;
        };
      }
    | {
        to: {
          units: DurationInputArg2;
          duration: number;
        };
        from: {
          units: DurationInputArg2;
          duration: number;
        };
      };
};

export type TenantSettings = {
  common_settings: {
    pin_metadata: Array<string>;
  };
  "serial-key": string | null;
  show_tabs: ShowTabsData | null | undefined;
  dashboard_settings: {
    custom_time_ranges: {
      [x: string]:
        | AbsoluteTimeRange
        | {
            to: {
              date: string;
            };
            from: {
              date: string;
            };
          }
        | {
            to: {
              units: DurationInputArg2;
              duration: number;
            };
            from: {
              units: DurationInputArg2;
              duration: number;
            };
          };
    };
  };
  "device-type"?: string;
  hardware_type: string;
  default_action_timeout?: number | Date;
  logo: {
    light: string;
    dark: string;
  };
  favicon: {
    light: string;
    dark: string;
  };
};

export type UserSettings = {
  theme: string;
  current_release_id: string;
  changelog_id: string;
  get_started: {
    deviceType: {
      [deviceType: string]: {
        [key: string]: boolean;
      };
    };
    selectedDevice: string | undefined;
    skip: boolean;
  };
};

export type User = {
  id: string;
  name: string;
  email: string;
  role: Role;
  tenants: Array<string>;
  tenant_id?: string;
  "tenant-settings"?: TenantSettings;
  settings?: UserSettings;
};

export interface IUser {
  name: string;
  email: string;
  roles: [number];
}

export interface IUserResult {
  result: { [key: string]: IUser };
}

export type Settings = {
  vin_number?: string;
  tenancy: "multi" | "single";
  default_tenant: string;
};

export type ActionsNavbarActiveItemType =
  | "Action Details"
  | "Inventory"
  | "New Action"
  | "Action Settings";

export type ActionType = {
  type: string;
  icon: SemanticICONS;
  payload_type: string;
  json_schema?: string | null;
  json_ui_schema?: string | null;
  timeout?: number | Date | null;
};

export interface Auth {
  url: string;
  type: string;
}

export function timeoutDelay(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}

// ====================== Archbee Pages Id ======================
export const urlToDocId = {
  // ========================= Device Management ===============================
  devices:
    "/platform-guide/device-management/introduction-to-device-management",

  // ========================= Dashboard ===============================
  dashboards: "/platform-guide/dashboards/introduction-to-dashboards",
  "compare-dashboards":
    "/platform-guide/dashboards/compare-dashboard#using-the-compare-dashboard-feature",

  // =========================  Actions ===============================
  actions: "/platform-guide/actions/action-details/introduction",
  "live-actions": "/platform-guide/actions/action-details/live-actions-list",
  inventory: "/platform-guide/actions/inventory-management/introduction",
  "firmware-files": "/platform-guide/actions/inventory-management/introduction",
  "json-configurations":
    "/platform-guide/actions/inventory-management/introduction",
  "geofence-configurations":
    "/platform-guide/actions/inventory-management/introduction",
  "new-action": "/platform-guide/actions/action-details/triggering-new-action",
  "action-settings":
    "/platform-guide/action-types/introduction-to-action-types",

  // ========================= Alerts ===============================
  alerts:
    "/platform-guide/alert-system/introduction-to-alerts-and-notifications",
  "alert-rules":
    "/platform-guide/alert-system/introduction-to-alerts-and-notifications",
  "alert-notification":
    "/platform-guide/alert-system/introduction-to-alerts-and-notifications",

  // ========================= Settings ===============================
  general: "/platform-guide/general-settings",
  streams: "/platform-guide/streams-tables/introduction-to-streams",
  users: "/platform-guide/users/introduction-to-users-in-projects",
  roles: "/platform-guide/roles/role-based-access-control",
  metadata: "/platform-guide/metadata/introduction-to-metadata-keys",
  api_keys: "/platform-guide/api-keys/introduction-to-api-keys",
  session_types: "/platform-guide/session-types/introduction-to-session-types",
  dbc: "/platform-guide/dbc-parsers/introduction-to-dbc-parsers",
  device_components:
    "/platform-guide/device-components/introduction-to-device-components",
};
// ==================== Archbee Pages Id Ends ====================

export function dropDownOptionsFromArray(array: string[]) {
  let options = [
    {
      key: "",
      value: "",
      text: "No options available",
    },
  ];
  if (array !== undefined && array.length > 0) {
    options = array.map((s) => {
      return {
        key: s,
        value: s,
        text: s,
      };
    });
  }
  return options;
}
export const currentReleaseId = "r36-03-dec-2025"; // This should be updated with every release
export const currentReleaseVersion = "R36"; // This should be updated with every release
//This should be the last part of the URL of the changelog page in Archbee

// Email ID validation
export function validateEmail(email: string): boolean {
  // Validates that the email address starts with one or more characters that are not whitespace or '@',
  // followed by the '@' symbol, followed by one or more characters that are not whitespace or '@',
  // and ends with a dot ('.') followed by one or more characters that are not whitespace or '@'
  const emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // [^\s@]+ Matches one or more characters that are not whitespace or '@'
  return emailRegex.test(email);
}

// Email ID validation, more restricted
export const validateRestrictedEmail = (emailString: string): boolean => {
  const restrictedEmailRegex: RegExp =
    /^(?!.*\.{2})[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return restrictedEmailRegex.test(emailString);
};

// Whole Number Validation
export function validateWholeNumber(number: string): boolean {
  const wholeNumberRegex: RegExp = /^\d+$/;
  return wholeNumberRegex.test(number);
}

/**
 * Validates if the given string is an integer number.
 * The integer number can be positive or negative.
 *
 * @param number - The string to be validated as an integer.
 * @returns A boolean indicating whether the string is a valid integer.
 */
export function validateIntegerNumber(number: string): boolean {
  const integerRegex: RegExp = /^-?\d+$/;
  return integerRegex.test(number);
}

/**
 * Validates if the given string is a valid integer or floating-point number.
 *
 * The function uses a regular expression to check if the input string
 * represents a valid integer or floating-point number. The accepted formats are:
 * - Integers (e.g., "123")
 * - Floating-point numbers with leading digits (e.g., "123.456")
 * - Floating-point numbers without leading digits (e.g., ".456")
 * - Zero with or without decimal part (e.g., "0", "0.456")
 *
 * @param number - The string to validate.
 * @returns `true` if the string is a valid integer or floating-point number, otherwise `false`.
 */
export function validateIntOrFloatNumber(number: string): boolean {
  const floatNumberRegex: RegExp =
    /^([1-9]\d*(\.)\d*|0?(\.)\d*[1-9]\d*|[0-9]\d*)$/;
  return floatNumberRegex.test(number);
}

// Phone Number validation
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  // This regex enforces the presence of a plus sign at the beginning (^\+), allows for 1 to 3 digits for the country code ([0-9]{1,3})
  // The rest of the digits are matched using \d{6,14}. This pattern provides more flexibility for different formats of phone numbers while ensuring the plus sign for the country code.
  const phoneRegex: RegExp = /^\+[1-9]\d{0,2}\d{6,12}$/;
  return phoneRegex.test(phoneNumber);
};

// Name validation
export function validateName(name: string): boolean {
  // [a-zA-ZÀ-ÖØ-öø-ÿ']+ Matches one or more alphabetic characters (uppercase or lowercase), accented characters, apostrophes, or hyphens.
  const nameRegex: RegExp = /^[a-zA-ZÀ-ÖØ-öø-ÿ']+(\s[a-zA-ZÀ-ÖØ-öø-ÿ']+)*$/;
  return nameRegex.test(name);
}

/**
 * Validates if a string contains only alphanumeric characters
 *
 * @param {string} name - The string to be validated.
 * @returns {boolean} True if the string contains only alphanumeric characters and name contains at least one character, false otherwise.
 */
export function onlyAlphanumericValidation(name: string): boolean {
  const alphanumericRegex: RegExp = /^[a-zA-Z0-9]+$/;
  return alphanumericRegex.test(name);
}

/**
 * Validates if a string contains only alphanumeric characters and underscores.
 *
 * @param {string} name - The string to be validated.
 * @returns {boolean} True if the string contains only alphanumeric characters and underscores, false otherwise.
 */
export function specialCharacterValidation(name: string): boolean {
  const specialCharacterRegex: RegExp = /^[a-zA-Z0-9_\s]*$/;
  return specialCharacterRegex.test(name);
}

/**
 * Validates if a string contains a valid URL.
 * This function uses the URL constructor to validate the URL, with additional checks for the hostname and domainPattern.
 * @param {string} url - The URL to be validated.
 * @returns {boolean} True if the URL is valid, false otherwise.
 */
export const isURLValid = (url: string): boolean => {
  try {
    const parsedURL = new URL(url);

    // Check for a valid protocol and hostname
    if (!parsedURL.protocol || !parsedURL.hostname) {
      return false;
    }

    // Check if the hostname is a valid domain
    // This checks for a domain with a top-level domain (TLD) of at least 2 characters
    // `^(?:[a-zA-Z0-9-]+\.)+` - This part of the pattern ensures that there is at least one subdomain. It matches one or more segments of the domain part separated by dots. Each segment can include alphanumeric characters and hyphens.
    // `[a-zA-Z]{2,}$` - This part matches the top-level domain (TLD), which must be at least two characters long.
    const domainPattern: RegExp = /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    if (!domainPattern.test(parsedURL.hostname)) {
      return false;
    }

    return true;
  } catch (error) {
    console.log("URL is invalid, ", error);
    return false;
  }
};

// Hiding Get Started for enterprise users
export function isHostMicelio(): boolean {
  // stage,and localhost are added for testing purposes only
  return ["localhost", "stage", "micelio", "numeros"].some((host) =>
    window.location.host.includes(host)
  );
}

// Hiding tabs for micelio and numeros.
export function isHostMicelioHideTabs(): boolean {
  return ["micelio", "numeros"].some((host) =>
    window.location.host.includes(host)
  );
}

export function enableDBCParserByDefault(): boolean {
  return ["localhost", "exponent", "matter", "galaxy", "galaxy-prod"].some(
    (host) => window.location.host.includes(host)
  );
}

export function convertToURLParams(values: Object) {
  const e = Object.entries(values).map(([key, values]) => [
    key,
    Array.isArray(values) ? values.join(",") : values,
  ]);

  return new URLSearchParams(Object.fromEntries(e));
}

/**
 * @param state - The state name to generate color for
 * @returns Hex color string (e.g., "#a3b2c1")
 */
export function hashStringToColor(state: string): string {
  // Create hash from state name using simple djb2 hash algorithm
  let hash = 0;
  for (let i = 0; i < state.length; i++) {
    const char = state.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convert hash to hex color (0x000000 to 0xFFFFFF)
  const color = Math.abs(hash) % 16777215;
  return `#${color.toString(16).padStart(6, "0")}`;
}
